'use client';

import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { BottomSheet, Button } from '../../../../components/ui';
import { Credits } from '../../../../components/Credits';
import { UserAvatar } from '../../../../components/UserAvatar';
import { Spinner } from '../../../../components/ui/Spinner';
import { useLocalTranslation } from '../../../../hooks/useLocalTranslation';
import type { Team } from '@cs-shot/battle-sim';
import { ModeBadge, useModeRule } from '../../../../components/caseBattle/atoms';
import { TwoSideSelector } from '../../../../components/RushMid/game/TwoSideSelector';
import { seatsBySlot, sideOf } from '../../../../components/caseBattle/battleModel';
import { useCaseBattleViewers } from '../../../../queries/useCaseBattleQueries';
import type { CaseBattleDetailsResponse } from '../../../../services/generated/Api';
import translations from '../../BattleDetailsScreen.translations';

interface SheetProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ModeRuleSheet: FC<SheetProps & { mode: string }> = ({ isOpen, onClose, mode }) => {
  const { t } = useLocalTranslation(translations);
  const modeRule = useModeRule();
  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title={t('modeRuleTitle')} heightClass="h-auto max-h-[60vh]">
      <div className="flex flex-col items-center gap-3 px-2 pb-4 pt-2 text-center">
        <ModeBadge mode={mode} className="h-7 px-3 text-[14px]" />
        <p className="text-[15px] text-label-secondary">{modeRule(mode)}</p>
      </div>
    </BottomSheet>
  );
};

export const BattleViewersSheet: FC<SheetProps & { battleId: string; count: number }> = ({ isOpen, onClose, battleId, count }) => {
  const { t } = useLocalTranslation(translations);
  const { data, isLoading } = useCaseBattleViewers(battleId, isOpen);
  const viewers = data?.viewers ?? [];
  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title={`${t('viewersTitle')} · ${count}`} heightClass="h-auto max-h-[70vh]">
      <div className="flex flex-col pb-4">
        {isLoading && (
          <div className="flex justify-center py-8">
            <Spinner size="s" />
          </div>
        )}
        {!isLoading && viewers.length === 0 && <p className="py-8 text-center text-[14px] text-label-secondary">{t('viewersEmpty')}</p>}
        {viewers.map((viewer) => (
          <div key={viewer.userId} className="flex items-center gap-3 py-2">
            <UserAvatar userId={viewer.userId} firstName={viewer.firstName} level={viewer.level} size="s" />
            <span className="flex min-w-0 flex-1 flex-col leading-tight">
              <span className="truncate text-[15px] font-semibold text-label-primary">{viewer.firstName || t('playerFallback')}</span>
              <span className="text-[12px] text-label-secondary">{viewer.participant ? t('viewerPlaying') : t('viewerWatching')}</span>
            </span>
          </div>
        ))}
      </div>
    </BottomSheet>
  );
};

export type JoinableBattle = Pick<CaseBattleDetailsResponse, 'maxPlayers' | 'isTeam' | 'totalCost'> & {
  mode: string;
  participants: Pick<CaseBattleDetailsResponse['participants'][number], 'slot'>[];
};

interface JoinConfirmSheetProps extends SheetProps {
  battle: JoinableBattle;
  initialSlot: number | null;
  pending: boolean;
  onConfirm: (slot: number | undefined) => void;
}

export const JoinConfirmSheet: FC<JoinConfirmSheetProps> = ({ isOpen, onClose, battle, initialSlot, pending, onConfirm }) => {
  const { t } = useLocalTranslation(translations);
  const modeRule = useModeRule();
  const seats = seatsBySlot(battle.maxPlayers, battle.participants);
  const [side, setSide] = useState<Team | null>(null);

  const freeSlotOn = (team: Team): number | null => {
    const from = team === 'ct' ? 0 : 2;
    const free = [from, from + 1].find((s) => seats[s] === undefined);
    return free ?? null;
  };

  const defaultSide: Team | null = freeSlotOn('ct') !== null ? 'ct' : freeSlotOn('t') !== null ? 't' : null;

  useEffect(() => {
    if (isOpen) setSide(initialSlot !== null ? (sideOf(initialSlot) === 0 ? 'ct' : 't') : defaultSide);
  }, [isOpen, initialSlot, defaultSide]);

  const slot = battle.isTeam && side ? freeSlotOn(side) : null;
  const canConfirm = !battle.isTeam || slot !== null;

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title={t('joinConfirmTitle')} heightClass="h-auto max-h-[85vh]">
      <div className="flex flex-col gap-4 pb-2">
        <div className="flex flex-col items-center gap-2 text-center">
          <ModeBadge mode={battle.mode} className="h-7 px-3 text-[14px]" />
          <p className="text-[15px] text-label-secondary">{modeRule(battle.mode)}</p>
        </div>

        {battle.isTeam && (
          <div className="flex flex-col gap-2">
            <p className="text-[15px] font-semibold text-label-primary">{t('chooseSide')}</p>
            <TwoSideSelector side={side ?? 'ct'} onPick={setSide} />
            {side && freeSlotOn(side) === null && <p className="text-center text-[13px] text-accent-red">{t('sideFull')}</p>}
          </div>
        )}

        <div className="flex flex-col gap-2">
          <Button
            size="l"
            stretched
            variant="cta"
            disabled={!canConfirm}
            loading={pending}
            onClick={() => onConfirm(slot ?? undefined)}
          >
            {t('join')} <Credits amount={battle.totalCost} iconSize={14} />
          </Button>
          <Button size="l" stretched variant="tertiary" onClick={onClose}>
            {t('cancel')}
          </Button>
        </div>
      </div>
    </BottomSheet>
  );
};

interface SellAllConfirmSheetProps extends SheetProps {
  amount: number;
  pending: boolean;
  onConfirm: () => void;
}

export const SellAllConfirmSheet: FC<SellAllConfirmSheetProps> = ({ isOpen, onClose, amount, pending, onConfirm }) => {
  const { t } = useLocalTranslation(translations);
  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title={t('sellConfirmTitle')} heightClass="h-auto max-h-[60vh]">
      <div className="flex flex-col gap-4 px-4 pb-6 pt-1">
        <p className="text-center text-[15px] text-label-secondary">{t('sellConfirmText')}</p>
        <div className="flex flex-col gap-2">
          <Button size="l" stretched variant="cta" loading={pending} onClick={onConfirm} data-sound="cta">
            {t('sellDrops')} <Credits amount={amount} iconSize={14} />
          </Button>
          <Button size="l" stretched variant="tertiary" onClick={onClose} disabled={pending}>
            {t('cancel')}
          </Button>
        </div>
      </div>
    </BottomSheet>
  );
};
